import { useState } from 'react';
import type { CheckResourcesResponse, Principal } from '@cerbos/core';
import { useCerbos } from '@cerbos/react';
import useDeepCompareEffect from 'react-use/esm/useDeepCompareEffect';
import useNetworkState from 'react-use/esm/useNetworkState';
import type { BaseAccessCheckResult, ResourceConfig } from '@skynet/authz-core';
import { ANY_ID, createResourceChecks, getPermissions } from '@skynet/authz-core';
import { CerbosClient, getPrincipal } from '@skynet/authz-provider';
import { getTokenPayload } from '@skynet/data-auth-api';

import { EsApiKeyActions, EsApiKeyCommands } from './types';

interface TradingApiKeyAccessCheckProps {
  scope: string;
}

type PermissionKey = 'canAddApiKey' | 'canUpdateApiKey' | 'canDeleteApiKey' | 'canDeleteDexPair';
interface BasePermissions extends Record<PermissionKey, boolean> {}
export interface TradingApiKeyAccessCheckResult extends BasePermissions, BaseAccessCheckResult {}

const permissionConfigs: ResourceConfig[] = [
  { kind: EsApiKeyCommands.CreateESAPIKey, actionMap: { canAddApiKey: EsApiKeyActions.Create } },
  { kind: EsApiKeyCommands.UpdateESAPIKey, actionMap: { canUpdateApiKey: EsApiKeyActions.Update } },
  { kind: EsApiKeyCommands.DeleteESAPIKey, actionMap: { canDeleteApiKey: EsApiKeyActions.Delete } },
  {
    kind: EsApiKeyCommands.DeleteESDEXPair,
    actionMap: { canDeleteDexPair: EsApiKeyActions.Delete },
  },
];

interface CachedResponse {
  promise: Promise<CheckResourcesResponse>;
  timestamp: number;
}

const cerbosCache: Record<string, CachedResponse> = {};

async function checkResourcesCached(
  principal: Principal,
  id: string,
  scope: string,
): Promise<CheckResourcesResponse> {
  const key = `${JSON.stringify(principal)}-${id}-${scope}`;
  const cached = cerbosCache[key];
  const now = Date.now();

  if (cached && now - cached.timestamp < 5 * 60 * 1000) {
    return cached.promise;
  } else {
    delete cerbosCache[key];
  }

  const resources = createResourceChecks({ configs: permissionConfigs, resourceScope: scope });
  const promise = CerbosClient.checkResources({ principal, resources });
  cerbosCache[key] = { promise, timestamp: now };

  return promise;
}

const defaultPermissions: BasePermissions = {
  canAddApiKey: false,
  canUpdateApiKey: false,
  canDeleteApiKey: false,
  canDeleteDexPair: false,
};

export async function checkTradingAddApiKeyAccess({
  id = ANY_ID,
  accessToken,
  scope,
}: {
  id?: string;
  accessToken?: string;
  scope: string;
}): Promise<BasePermissions> {
  if (!accessToken) {
    return defaultPermissions;
  }

  try {
    const principal = getPrincipal(getTokenPayload(accessToken));
    const response = await checkResourcesCached(principal, id, scope);
    const perms = getPermissions<BasePermissions>({
      permissionConfigs,
      data: response,
      id,
      scope,
    });
    return { ...defaultPermissions, ...perms };
  } catch (error) {
    console.error('Cerbos authorization check failed:', {
      context: 'checkTradingAddApiKeyAccess',
      id,
      scope,
      error: error instanceof Error ? error.message : 'Unknown error',
    });
    return defaultPermissions;
  }
}

export function useTradingApiKeyAccessCheck({
  scope,
}: TradingApiKeyAccessCheckProps): TradingApiKeyAccessCheckResult {
  const id = ANY_ID;
  const { principal } = useCerbos();
  const { online } = useNetworkState();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(defaultPermissions);
  const [error, setError] = useState<Error>();

  useDeepCompareEffect(() => {
    if (principal && online && id) {
      const abortController = new AbortController();
      checkResourcesCached(principal, id, scope)
        .then((response) => {
          if (!abortController.signal.aborted) {
            setIsLoading(false);
            const perms = getPermissions({ permissionConfigs, data: response, id, scope });
            setData({ ...defaultPermissions, ...perms });
            setError(undefined);
          }
          return response;
        })
        .catch((err: unknown) => {
          if (!abortController.signal.aborted) {
            setIsLoading(false);
            setData(defaultPermissions);
            setError(
              err instanceof Error
                ? err
                : new Error('An unexpected error occurred', { cause: err }),
            );
          }
        });

      return (): void => {
        abortController.abort();
      };
    }
    return undefined;
  }, [principal, online, id]);

  return { ...data, isLoading, error };
}
